import { DeleteOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { Button, Table, Tag, Tooltip } from 'antd';

import { Schemas } from '@api-client/generated/types';
import { AccessRightsChips } from '@components';
import { fullName, initials, roleLabel } from '@utils';

import * as S from './styled';

type Membership = Schemas.AdminMembershipRecordDto;

type Props = {
  members: Membership[];
  isLoading: boolean;
  isDetaching: boolean;
  onDetach: (member: Membership) => void;
};

const CompanyTeamRoster = ({
  members,
  isLoading,
  isDetaching,
  onDetach,
}: Props) => {
  const columns: TableProps<Membership>['columns'] = [
    {
      title: <S.ColumnTitle>Member</S.ColumnTitle>,
      key: 'member',
      render: (_, member) => (
        <S.MemberCell align="center" gap={12}>
          <S.Avatar align="center" justify="center">
            {initials(member)}
          </S.Avatar>
          <S.MemberCell vertical gap={2}>
            <S.Name>{fullName(member)}</S.Name>
            <S.Email>{member.email}</S.Email>
          </S.MemberCell>
        </S.MemberCell>
      ),
    },
    {
      title: <S.ColumnTitle>Role</S.ColumnTitle>,
      key: 'role',
      render: (_, member) => (
        <Tag bordered={false}>{roleLabel(member.adminRole)}</Tag>
      ),
    },
    {
      title: <S.ColumnTitle>Access</S.ColumnTitle>,
      key: 'access',
      render: (_, member) => <AccessRightsChips rights={member.accessRights} />,
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, member) => (
        <Tooltip
          title={
            member.canRemove
              ? undefined
              : 'The last member of a company can’t be removed'
          }
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={!member.canRemove || isDetaching}
            onClick={() => onDetach(member)}
            aria-label={`Remove ${fullName(member)}`}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={members}
      loading={isLoading}
      pagination={false}
      rowKey={(member) => member.userId}
    />
  );
};

export default CompanyTeamRoster;
